import {
  SanityPrintType,
  SanityPrintFeeCombination,
  SanityPrintSlotType,
} from '@data/sanity/queries/types/print'
import {
  EnabledStatus,
  SlotConfiguration,
  SlotWithConfiguration,
} from './types'
import { getAllCombinations } from '@lib/helpers'
import { SanityShopPrint } from '@data/sanity/queries/types/shop'

const sampleText = 'a'

const sampleShopPrint: SanityShopPrint = {
  _key: sampleText,
  title: sampleText,
  printSku: sampleText,
  slot: {
    _id: sampleText,
    type: 'front',
    name: sampleText,
  },
  type: 'logo',
}

/**
 * Gets IDs for all print slot + print type combinations based on print fee settings.
 */
const getPrintFeeIdCombinations = (
  printFeeCombination: SanityPrintFeeCombination,
) => {
  const printFeeSlotIds = printFeeCombination.printFeeCombinationItems.map(
    (item) => item.slot._id,
  )
  const printFeeTypes = printFeeCombination.printFeeCombinationItems.map(
    (item) => item.type,
  )

  const printFeeIdCombinations = getAllCombinations(
    printFeeSlotIds,
    printFeeTypes,
  )

  return printFeeIdCombinations.map((combination) => combination.join('|'))
}

/**
 * Determines whether to apply print fee combination to print slot.
 */
const getIsFeeApplicableToSlot = (
  printFeeCombination: SanityPrintFeeCombination,
  slot: SlotWithConfiguration,
) => {
  // Filter items by slot type and get print types
  const filteredItems = printFeeCombination.printFeeCombinationItems.filter(
    (item) => item.slot.type === slot.type,
  )
  const printTypes = filteredItems.map((item) => item.type)

  // Check that slot configuration is valid and combination item has matching slot type and print type
  const isLogoFeeApplicable =
    printTypes.includes('logo') &&
    slot.configuration.logoEnabled === 'yes' &&
    slot.configuration.logo
  const isImageFeeApplicable =
    printTypes.includes('image') &&
    slot.configuration.imageEnabled === 'yes' &&
    slot.configuration.image
  const isTextFeeApplicable =
    printTypes.includes('text') &&
    slot.configuration.freetextEnabled === 'yes' &&
    slot.configuration.freetext

  return isLogoFeeApplicable || isImageFeeApplicable || isTextFeeApplicable
}

/**
 * Determines whether to apply print fee combination to print slots.
 * All print slots (front, back) must have at least 1 applicable fee for print type (logo, image, text).
 */
const getIsFeeApplicableToSlots = (
  printFeeCombination: SanityPrintFeeCombination,
  slots: SlotWithConfiguration[],
) => {
  return slots.every((slot) => {
    return getIsFeeApplicableToSlot(printFeeCombination, slot)
  })
}

/**
 * Gets active print fee combinations for print slots.
 */
export const getActivePrintFeeCombinations = (
  printFeeCombinations: SanityPrintFeeCombination[],
  slots: SlotWithConfiguration[],
) => {
  const appliedPrintFeeCombinations: SanityPrintFeeCombination[] = []
  const appliedIds: string[] = []

  // Find print fees that can be applied to current slots and configuration
  printFeeCombinations.forEach((printFeeCombination) => {
    // Check if fee is already applied (to any item in the combination)
    const printFeeIds = getPrintFeeIdCombinations(printFeeCombination)
    const isFeeApplied = printFeeIds.some((printFeeId) =>
      appliedIds.includes(printFeeId),
    )

    if (isFeeApplied) {
      return
    }

    // Check if fee is applicable
    const printFeeSlotIds = printFeeCombination.printFeeCombinationItems.map(
      (item) => item.slot._id,
    )
    const filteredSlots = slots.filter((slot) =>
      printFeeSlotIds.includes(slot._id),
    )
    const isFeeApplicable = getIsFeeApplicableToSlots(
      printFeeCombination,
      filteredSlots,
    )

    if (!isFeeApplicable) {
      return
    }

    // Apply print fee
    appliedPrintFeeCombinations.push(printFeeCombination)

    printFeeIds.forEach((printFeeId) => {
      appliedIds.push(printFeeId)
    })
  })

  return appliedPrintFeeCombinations
}

/**
 * Gets slot configuration with a mock value to simulate slot being filled.
 */
const getSlotConfigurationWithValue = (
  slotConfiguration: SlotConfiguration,
  printType: SanityPrintType,
): SlotConfiguration => {
  switch (printType) {
    case 'logo': {
      return {
        ...slotConfiguration,
        logo: sampleShopPrint,
        logoEnabled: 'yes',
      }
    }

    case 'image': {
      return {
        ...slotConfiguration,
        image: sampleShopPrint,
        imageEnabled: 'yes',
      }
    }

    case 'text': {
      return {
        ...slotConfiguration,
        freetext: sampleText,
        freetextEnabled: 'yes',
      }
    }
  }
}

/**
 * Gets print fee price for the specified print slot and print type.
 */
export const getPrintFeePrice = (
  printFeeCombinations: SanityPrintFeeCombination[],
  slots: SlotWithConfiguration[],
  activeSlotIndex: number,
  printType: SanityPrintType,
) => {
  // Insert mock value for print type into active slot configuration
  const updatedSlots = slots.map((slot, index) => {
    if (index !== activeSlotIndex) {
      return slot
    }

    return {
      ...slot,
      configuration: getSlotConfigurationWithValue(
        slot.configuration,
        printType,
      ),
    }
  })

  // Get active print fees for slot configuration with mock value
  const activePrintFeeCombinations = getActivePrintFeeCombinations(
    printFeeCombinations,
    updatedSlots,
  )

  // Find product variant for the given print slot and print type
  for (const printFeeCombination of activePrintFeeCombinations) {
    for (const item of printFeeCombination.printFeeCombinationItems) {
      if (
        item.slot._id === slots[activeSlotIndex]._id &&
        item.type === printType
      ) {
        const price = item.productVariant.comparePrice
          ? item.productVariant.comparePrice
          : item.productVariant.price
        return price
      }
    }
  }
}

/**
 * Gets total price of given print fees.
 */
export const getPrintFeeTotalPrice = (
  printFeeCombinations: SanityPrintFeeCombination[],
) => {
  return printFeeCombinations.reduce((total, printFeeCombination) => {
    const subTotal = printFeeCombination.printFeeCombinationItems.reduce(
      (innerTotal, item) => {
        const price = item.productVariant.comparePrice
          ? item.productVariant.comparePrice
          : item.productVariant.price

        return price ? innerTotal + price : innerTotal
      },
      0,
    )

    return total + subTotal
  }, 0)
}
